import { Component, Input, OnInit } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { MessageService } from 'primeng/api';
import { BuildType } from 'src/app/enums/build-type';
import { AssetTypes } from 'src/app/entities/enums/AssetTypes';
import { CommonEntityService } from 'src/app/common/services/common-entity.service';
import { BuildService } from 'src/app/build-data/services/build.service';

@Component({
  selector: 'app-misc-image-build-component',
  templateUrl: './misc-image-build-component.component.html',
  styleUrls: ['./misc-image-build-component.component.sass']
})
export class MiscImageBuildComponentComponent implements OnInit {
  private static readonly PROD_PREFIX_REGEX = /^prod\//;

  constructor(
    private commonEntityService: CommonEntityService,
    private buildService: BuildService,
    private clipboard: Clipboard,
    private messageService: MessageService,
  ) {
    
  }
  @Input() field: any;
  @Input() record: any;
  @Input() type: 'ref' | 'self';

  assetRecord: any;
  payloadBuildType: BuildType;
  
  get buildType(): typeof BuildType {
    return BuildType;
  }

  get assetType(): typeof AssetTypes {
    return AssetTypes;
  }

  ngOnInit(): void {
    if (!this.field || !this.record) {
      console.error('Field or record is not defined.');
      return;
    }

    if (this.type === 'self') {
      this.assetRecord = this.record;
    } else if (this.type === 'ref' && this.field.key && this.record.hasOwnProperty(this.field.key)) {
      this.assetRecord = this.record[this.field.key];
    } else {
      console.error(`The provided type(${this.type}) is neither "self" nor "ref", or the provided key (${this.field.key}) does not exist in the record.`);
    }

    console.log('Asset Record:', this.assetRecord);

    this.resolveBuildData();

    // Set buildAssetType based on assetRecord.assetType
    if (this.assetRecord?.entityType === BuildType.Items) {
      this.payloadBuildType = BuildType.Items;
    } else if (this.assetRecord?.entityType === BuildType.Levels) {
      this.payloadBuildType = BuildType.Levels;
    } else {
      this.payloadBuildType = BuildType.Images; 
    }
  }

  getAssetType(assetType: number): string {
    return AssetTypes[assetType] || 'Unknown';
  }

  /** Asset-type records only populate asset_versions; image-type records only populate image_versions. */
  getVersions(entity: any): any[] {
    return entity?.asset_versions?.length ? entity.asset_versions : entity?.image_versions;
  }

  getLastVersionHash(entity: any): string | null {
    const versions = this.getVersions(entity);
    return versions?.length > 0 ? versions[versions.length - 1].destinationPath : null;
  }

  /**
   * Resolves the platform to build-record-id map on assetRecord.buildData
   * into the array of build records `<build-data>` expects to render.
   */
  async resolveBuildData(): Promise<void> {
    if (Array.isArray(this.assetRecord?.buildData)) {
      // Already resolved (the parent dynamic view resolves misc asset keys too).
      return;
    }
    if (this.assetRecord && this.assetRecord.buildData) {
      const response = await this.buildService.getBuildData(this.assetRecord.buildData);
      this.assetRecord.buildData = response.buildData;
      this.assetRecord.buildStatus = response.buildStatus;
    } else if (this.assetRecord) {
      this.assetRecord.buildData = [];
    }
  }

  isUptoDate(entity: any): boolean {
    const currentHash = this.getLastVersionHash(entity);
    if (!currentHash || !entity?.lastHash) {
      return false;
    }
    return currentHash.replace(MiscImageBuildComponentComponent.PROD_PREFIX_REGEX, '')
      === entity.lastHash.replace(MiscImageBuildComponentComponent.PROD_PREFIX_REGEX, '');
  }

  getLastPromotedHash(entity: any): string {
    return entity?.lastPromotedHash || '';
  }

  getDynamicURL(id: string): string {
    return `/build-history/miscellaneous-build/${id}`;
  }

  openInNewTab(url: string): void {
    window.open(url, '_blank');
  }

  /**
   * Re-fetches the misc build after a build is queued and re-resolves its build data.
   * Uses assetRecord, not record: in 'ref' mode record is the parent entity.
   */
  getUpdatedBuildData(): void {
    setTimeout(async () => {
      if (!this.assetRecord?.id) return;
      const latest = await this.commonEntityService.findOneWithQuery('miscellaneous-build', { query: { id: this.assetRecord.id } });
      if (latest) {
        this.assetRecord.buildData = latest.buildData;
      }
      await this.resolveBuildData();
    }, 500);
  }

  getPromotedAtFromVersions(versions: any[], lastHash: string): string | null {
    if (!versions || !lastHash) return null;
        const version = versions.find(v => v.destinationPath.endsWith(lastHash));
    return version?.promotedAt || null;
  }

  getBuildEntity(): any {
    return this.assetRecord || this.record;
  }

  /** Copy full filepath to clipboard (one-click copy for path field). */
  copyPath(): void {
    const path = this.assetRecord?.path || '';
    if (!path) return;
    this.clipboard.copy(path);
    this.messageService.add({ severity: 'success', summary: 'Copied', detail: 'Path copied to clipboard.' });
  }

  /** Copy asset info in format "Name, ID: ###, filename" for Jira etc. */
  copyAssetInfo(): void {
    const name = this.assetRecord?.name || '';
    const id = this.assetRecord?.id ?? '';
    const fileName = this.assetRecord?.fileName || this.assetRecord?.path?.split('/').pop() || this.assetRecord?.path || '';
    const text = `${name}, ID: ${id}, ${fileName}`.trim();
    if (!text || text === ', ID: ,') return;
    this.clipboard.copy(text);
    this.messageService.add({ severity: 'success', summary: 'Copied', detail: text });
  }
}
